'use client';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Section from '@repo/ui/src/components/Section';
import SearchAutocomplete from '@components/SearchAutocomplete';
import { searchSubmitted } from '@repo/ui/src/services/analytics';
import { MetaProps } from '@repo/ui/src/types/meta';
import Seo, { Props as SeoProps } from '@repo/ui/src/components/Seo';
import { useSearch, type Facet } from '@hooks/search/useSearch';
import { useSearchOverview } from '@hooks/search/useSearchOverview';
import { useSearchUrlState } from '@hooks/search/useSearchUrlState';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import SearchChips, { SearchChipTab } from '@repo/ui/src/components/SearchChips';
import { buildFilterConfigs } from './buildFilterConfigs';
import { buildFilter, SEARCH_OPTIONS } from './buildSearchRequest';
import SearchAllView from './_SearchAllView';
import SearchGridView from './_SearchGridView';
import { CARDS_SHOWN } from './_SearchSectionRow';
import Container from '@repo/ui/src/components/Container';
import { classNames } from '@repo/ui/src/utils/classNames';

interface TabProps {
  tabName: string;
  searchType: string;
  redirectUrl?: string;
}

interface Props {
  _meta: MetaProps;
  title: string;
  searchPlaceholder: string;
  body?: any;
  tabLabel: string;
  pageTabs: {
    tabs: Array<TabProps>;
    _meta: MetaProps;
  };
  resultTextSingular: string;
  resultTextPlural: string;
  slotContent: Array<SeoProps>;
  defaultSearchTerm?: string;
  resultsForText?: string;
}

const SearchResultsBody = ({
  title,
  searchPlaceholder,
  tabLabel,
  pageTabs,
  resultTextSingular,
  resultTextPlural,
  slotContent,
  defaultSearchTerm,
  resultsForText = 'Results for',
}: Props) => {
  const { t } = useTranslation();
  const tabs: Array<SearchChipTab> = pageTabs
    ? [...pageTabs.tabs].sort((a, b) => {
        if (a.searchType === 'All') return -1;
        if (b.searchType === 'All') return 1;
        if (a.searchType === 'Recipe') return -1;
        if (b.searchType === 'Recipe') return 1;
        return 0;
      })
    : [];

  const inputRef = useRef<HTMLInputElement>(null);

  const {
    searchTerm,
    searchType,
    page,
    activeFilters,
    setSearchTerm,
    setSearchType,
    toggleFilter,
    clearAllFilters,
  } = useSearchUrlState({ defaultSearchTerm, inputRef });

  const isAllView = !searchType || searchType === 'All';
  const pageNumber = Number(page) || 1;

  const seo = slotContent?.[0];

  const [facets, setFacets] = useState<Array<Facet>>([]);

  // Hit counts piggyback on the data request for the active view: the overview request in
  // the All view, and the type's own search request in the grid view.
  const {
    overview,
    isLoading: overviewLoading,
    isValidating: overviewValidating,
  } = useSearchOverview(isAllView ? searchTerm : '', CARDS_SHOWN);

  // Build filter configs from facets for the chip dropdowns
  const filterConfigs = useMemo(
    () => buildFilterConfigs(facets, searchType, t),
    [facets, searchType, t],
  );

  const {
    data: searchData,
    isLoading: searchLoading,
    isValidating: searchValidating,
    setSize,
    size: currentSize,
  } = useSearch({
    searchTerm: isAllView ? '' : searchTerm,
    searchType,
    initialSize: pageNumber,
    filter: buildFilter(searchType, activeFilters),
    options: SEARCH_OPTIONS[searchType],
  });

  const overviewCounts = Object.fromEntries(
    Object.entries(overview).map(([type, { nbHits }]) => [type, nbHits]),
  );
  const counts = isAllView ? overviewCounts : (searchData.counts ?? {});

  const onTypeChange = (type: string) => {
    // Clear stale facets immediately so previous type's filters don't flash
    setFacets([]);
    setSearchType(type);
  };

  useEffect(() => {
    searchSubmitted({ query: searchTerm });
  }, [searchTerm]);

  // Expose facets to the chip filter dropdowns.
  // Skip updates while loading so the chips don't disappear during refetches.
  const searchFacets = searchData.facets;
  useEffect(() => {
    if (searchValidating || !searchFacets) return;
    setFacets((prev) =>
      JSON.stringify(prev) === JSON.stringify(searchFacets) ? prev : searchFacets,
    );
  }, [searchFacets, searchValidating]);

  const contentTabs = tabs.filter((t) => !t.redirectUrl && t.searchType !== 'All');

  return (
    <>
      {seo && <Seo {...seo} />}
      <Section gutterX gutterY className="flex-1">
        <Container maxWidth="wide">
          <div className="mt-2 md:mt-10">
            <h1
              className={classNames(
                searchTerm ? 'tra-text-web-heading-4' : 'normal-case tra-text-web-body-xl',
              )}
            >
              {searchTerm
                ? `${resultsForText} "${searchTerm}"`
                : title || 'Search products, recipes, or support information'}
            </h1>
          </div>

          <div id="instructions" className="sr-only">
            Begin typing to search, use arrow keys to navigate, Enter to select
          </div>

          <SearchAutocomplete
            searchTerm={searchTerm}
            placeholder={searchPlaceholder}
            className="my-6"
            setSearchTerm={setSearchTerm}
            inputRef={inputRef}
          />

          {tabs?.length > 0 && searchTerm && (
            <div>
              <SearchChips
                tabs={tabs}
                activeType={searchType}
                searchTerm={searchTerm}
                tabLabel={tabLabel || 'Select a search type'}
                counts={counts}
                onTypeChange={onTypeChange}
                filterConfigs={filterConfigs}
                activeFilters={activeFilters}
                onFilterToggle={toggleFilter}
                onClearAll={clearAllFilters}
                clearAllLabel={t('search.clearAllFilters', undefined, {
                  default: 'Clear all filters',
                })}
                filtersLoading={!isAllView && searchLoading && filterConfigs.length === 0}
              />

              <div className="mt-12">
                {isAllView ? (
                  <SearchAllView
                    overview={overview}
                    isFetching={overviewLoading || overviewValidating}
                    contentTabs={contentTabs}
                    onViewAll={onTypeChange}
                  />
                ) : (
                  <SearchGridView
                    state={searchData}
                    searchType={searchType}
                    isLoading={searchLoading}
                    isValidating={searchValidating}
                    currentSize={currentSize}
                    resultTextSingular={resultTextSingular}
                    resultTextPlural={resultTextPlural}
                    onLoadMore={() => setSize(currentSize + 1)}
                  />
                )}
              </div>
            </div>
          )}
        </Container>
      </Section>
    </>
  );
};

export default SearchResultsBody;
